import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import {
  FaChartLine,
  FaLandmark,
  FaShoppingCart,
  FaGlobeAsia,
} from "react-icons/fa";
import "./homepage.css";

const Homepage = () => {
  // State for Interactive Maturity Stages
  const [activeStage, setActiveStage] = useState(1);

  // State for Service Capability Accordions
  const [openGrowthCap, setOpenGrowthCap] = useState(0);
  const [openPlatformCap, setOpenPlatformCap] = useState(0);

  // State for Industry Filter
  const [industryFilter, setIndustryFilter] = useState("All");

  // State for Briefing Call Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bookingStage, setBookingStage] = useState("Stage 1 — India-Curious");
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    email: "",
    org: "",
    location: "",
    service: "India Growth Engine",
    notes: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => {
      setFormSubmitted(false);
      setIsModalOpen(false);
    }, 2800);
  };

  // 11 Industry Verticals
  const industries = [
    {
      name: "Aerospace and Defense",
      category: "Industrial & Regulated",
      desc: "Offsets structuring, B2G procurement, and MoD tier-1 supply chain positioning.",
      icon: "✈️",
    },
    {
      name: "Semiconductors & Embedded",
      category: "Tech & Electronics",
      desc: "India Semiconductor Mission incentives, EMS partnerships, and VLSI talent hubs.",
      icon: "🔬",
    },
    {
      name: "Telecom and Networking",
      category: "Tech & Electronics",
      desc: "5G infrastructure, carrier integrations, and NOC insourcing programs.",
      icon: "📡",
    },
    {
      name: "Medical and Healthcare",
      category: "Industrial & Regulated",
      desc: "CDSCO regulatory pathways, MedTech manufacturing incentives, and hospital network access.",
      icon: "🩺",
    },
    {
      name: "Industrial Automation",
      category: "Industrial & Regulated",
      desc: "Smart factory integrations, OEM distributor networks, and Industry 4.0 pilots.",
      icon: "⚙️",
    },
    {
      name: "Automotive & Transportation",
      category: "Industrial & Regulated",
      desc: "EV ecosystem, FAME incentives, International Procurement Offices (IPO), and plant siting.",
      icon: "🚗",
    },
    {
      name: "Energy and Cleantech",
      category: "Energy & Infrastructure",
      desc: "Renewable energy tenders, grid storage compliance, and solar/wind supply chain integration.",
      icon: "⚡",
    },
    {
      name: "Computing & Data Storage",
      category: "Tech & Electronics",
      desc: "Hyperscale cloud data center compliance, edge computing, and hardware assembly setup.",
      icon: "💾",
    },
    {
      name: "Consumer and Lifestyle",
      category: "Consumer",
      desc: "Omnichannel retail, Amazon/Flipkart marketplace logic, and last-mile D2C distribution.",
      icon: "🛍️",
    },
    {
      name: "Smart Cities & Infrastructure",
      category: "Energy & Infrastructure",
      desc: "Public-private partnerships (PPP), state municipal tenders, and urban sensor networks.",
      icon: "🏙️",
    },
    {
      name: "Education Technology",
      category: "Consumer",
      desc: "University & engineering lab partnerships, edtech curriculum adaptation, and local EMS packaging.",
      icon: "🎓",
    },
  ];

  const filteredIndustries =
    industryFilter === "All"
      ? industries
      : industries.filter((i) => i.category === industryFilter);

  // Growth Engine Capabilities
  const growthCapabilities = [
    {
      title: "Commercial Strategy",
      summary:
        "India-specific pricing, packaging, and monetization design; buyer segmentation across enterprise, PSU, and SMB routes; channel economics analysis.",
    },
    {
      title: "Enterprise Sales & Key Account Growth",
      summary:
        "Priority CXO decision-maker access strategy, named account penetration playbooks, and governed CRM pipeline logic built for long procurement cycles.",
    },
    {
      title: "Public Sector, PSU & GeM Procurement",
      summary:
        "Government buyer mapping across central ministries, PSUs, and state bodies; GeM registration, catalog strategy, and tender bid response governance.",
    },
    {
      title: "Channel, Alliances & Partnership Models",
      summary:
        "Partner archetype mapping across distributors, systems integrators, and value-added resellers with margin structures and governance scorecards.",
    },
    {
      title: "Digital-First, E-Commerce & Distribution",
      summary:
        "Marketplace strategies on Amazon.in and Flipkart; D2C localization; last-mile logistics; and regulatory compliance for BIS, GST, and import licensing.",
    },
    {
      title: "Marketing, PR & Credibility Architecture",
      summary:
        "Adapting global brand narratives to the India buyer context, trade body engagement, analyst positioning, and executive thought leadership visibility.",
    },
  ];

  // Platform Capabilities
  const platformCapabilities = [
    {
      title: "Regional Hub & GCC Strategy",
      summary:
        "Operating model design for Centers of Excellence and GCCs; city and SEZ selection; cross-border reporting lines; transfer pricing and P&L governance.",
    },
    {
      title: "Innovation, Co-Creation & Developer Ecosystem",
      summary:
        "R&D center concept and hiring models; startup co-creation programs; IP governance frameworks; and developer ecosystem program architectures.",
    },
    {
      title: "Export from India & Supply Chain",
      summary:
        "Export readiness assessment; policy incentive mapping (PLI, RODTEP, EPCG, SPECS); FEMA customs structuring; and tier-1 supplier evaluation.",
    },
    {
      title: "Investment, M&A & Strategic Minority Stakes",
      summary:
        "Target identification; deal thesis development; partner fit assessment; FEMA & CCI regulatory approval pathway; and JV shareholder governance.",
    },
    {
      title: "PMO, RevOps & Performance Governance",
      summary:
        "Single accountable PMO charter, milestone ownership, weekly execution rhythm, RevOps pipeline dashboards, and structured HQ-India escalation protocols.",
    },
  ];

  // Maturity Stages Data
  const stagesData = [
    {
      stage: 1,
      name: "India-Curious",
      sub: "Exploration, Feasibility & Validation",
      headline: "Validate your India business case with precision.",
      desc: "For global leadership teams evaluating whether India is viable, where demand concentrates, and which regulatory or commercial hurdles must be sequenced first.",
      checks: [
        "Comprehensive market survey benchmarking 40+ competitors in-country",
        "Cost-to-breakeven and revenue potential modeling",
        "Regulatory & compliance posture assessment (FEMA, RBI, direct taxes)",
        "Tariff, incentive (PLI), and location fit analysis across SEZs and states",
      ],
      actionTitle: "Stage 1 Diagnostic Session",
      actionDesc:
        "In one 30-minute structured briefing, we review your thesis, outline viable routes, and flag critical sequencing risks.",
      buttonLabel: "Request Stage 1 Briefing",
    },
    {
      stage: 2,
      name: "India-Ready",
      sub: "Launch, Incorporation & Initial Commercial Wins",
      headline: "Establish your corporate entity and secure first revenue.",
      desc: "For companies that have decided to enter India and need an accountable partner to execute entity incorporation, regulatory licensing, initial customer access, and ground operations.",
      checks: [
        "Full entity incorporation (Wholly Owned Subsidiary, LLP, or Branch)",
        "Statutory compliance: FEMA, RBI clearances, GST, and MCA filings",
        "Named enterprise buyer pipeline and initial channel partner agreements",
        "Back-office setup: payroll, banking, accounting, and compliance governance",
      ],
      actionTitle: "Stage 2 Execution Activation",
      actionDesc:
        "Stand up your India Execution Office with defined milestone owners, weekly reporting, and live KPI dashboards.",
      buttonLabel: "Launch Stage 2 Execution",
    },
    {
      stage: 3,
      name: "India-Present",
      sub: "Scale, Insource & Expand Regional Operations",
      headline:
        "Optimize your existing India footprint for durable global value.",
      desc: "For multinational enterprises with an existing trading or outsourced presence in India that need to insource operations, set up GCCs/plants, or expand to regional hubs.",
      checks: [
        "Insourcing IT, NOC, and customer service operations from Tier-1 vendors",
        "Manufacturing plant siting, land acquisition, and 40+ statutory clearances",
        "Supply chain supplier rationalization (e.g. evaluating 480 down to 20)",
        "Establishing a regional operating hub for APAC and EMEA corridors",
      ],
      actionTitle: "Stage 3 Scale & Transfer",
      actionDesc:
        "Transform fragmented vendor dependencies into client-owned high-performance centers with 100% asset transfer.",
      buttonLabel: "Optimize Stage 3 Operations",
    },
  ];

  const currentStageInfo = stagesData.find((s) => s.stage === activeStage);

  return (
    <div className="scaleAX-home">
      {/* Ambient Glow Orbs */}
      <div className="bg-ambient-orb orb-1"></div>
      <div className="bg-ambient-orb orb-2"></div>
      <div className="bg-ambient-orb orb-3"></div>
      <div className="bg-ambient-orb orb-4"></div>
      <div className="bg-grid-pattern"></div>

      {/* ==========================================================================
          1. HERO SECTION
          ========================================================================== */}
      <section className="hero-section">
        <div className="home-container">
          <div className="hero-layout">
            {/* Left Content */}
            <div className="hero-content">
              {/* <div className="section-badge">
                <span className="badge-pulse"></span>
                Single Accountable India Execution Office
              </div> */}

              <h1 className="hero-main-title">
                India entry,
                <span className="gradient-title">built for revenue.</span>
              </h1>

              <p className="hero-lead-text">
                scaleAX helps global enterprises enter India, win revenue
                faster, and build operating capability on the ground - governed
                from day one across strategy, regulatory structuring, sales, and
                delivery.
              </p>

              <div className="hero-cta-group">
                <button
                  onClick={() => {
                    setBookingStage("Stage 1 — India-Curious");
                    setIsModalOpen(true);
                  }}
                  className="btn-primary-glow"
                >
                  Book a Briefing Call <span>↗</span>
                </button>
                <a href="#services" className="btn-secondary-glass">
                  Explore Service Lines <span>↓</span>
                </a>
              </div>

              {/* Trust Badges */}
              <div className="hero-trust-chips">
                <div className="trust-chip">
                  <span className="check-icon">✓</span> Senior Partner Led
                </div>
                <div className="trust-chip">
                  <span className="check-icon">✓</span> NDA on Request
                </div>
                <div className="trust-chip">
                  <span className="check-icon">✓</span> 24h Response
                </div>
                <div className="trust-chip">
                  <span className="check-icon">✓</span> US & India Operating HQ
                </div>
              </div>
            </div>

            {/* Right Visual Dashboard Card */}
            <img
              src="/images/hero-banner.jpg"
              alt="Native91"
              className="logo w-100"
            />
          </div>

          {/* Key Stats Strip */}
          <div className="hero-stats-strip">
            <div className="hero-stat-card">
              <div className="stat-number">15+ Yrs</div>
              <div className="stat-label">Cross-Border Experience</div>
              <div className="stat-desc">
                Deep corridor knowledge across regulatory, commercial, and
                ground operating layers.
              </div>
            </div>
            <div className="hero-stat-card">
              <div className="stat-number">11 Verticals</div>
              <div className="stat-label">Industry Expertise</div>
              <div className="stat-desc">
                Aerospace, semiconductors, medtech, telecom, automotive,
                cleantech, and industrial sectors.
              </div>
            </div>
            <div className="hero-stat-card">
              <div className="stat-number">5 Corridors</div>
              <div className="stat-label">Global Footprint</div>
              <div className="stat-desc">
                Active client and governance presence across US, UK, Europe,
                Japan, and India.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          2. CORE SERVICES SECTION
          ========================================================================== */}
      <section id="services" className="services-section">
        <div className="home-container">
          <div className="section-header">
            <div className="section-badge">
              {/* <span className="badge-pulse"></span> */}
              Two Integrated Service Lines
            </div>
            <h2 className="section-title">
              One partner across the{" "}
              <span className="gradient-text">India journey</span>
            </h2>
            <p className="section-desc">
              Two integrated service lines cover everything from first revenue
              in the domestic market to building India into a long-term global
              platform.
            </p>
          </div>

          <div className="service-lines-grid">
            {/* Service Line 1: India Growth Engine */}
            <div className="service-line-card">
              <div className="service-tag-row">
                <span className="section-badge" style={{ marginBottom: 0 }}>
                  Commercial GTM & Revenue
                </span>
                <span className="service-num">SERVICE 01</span>
              </div>
              <h3 className="service-card-title">India Growth Engine</h3>
              <p className="service-card-tagline">
                Building the commercial infrastructure to win revenue in India
                across enterprise, public sector (GeM), channel, and digital
                routes.
              </p>

              <div className="capabilities-list">
                {growthCapabilities.map((cap, idx) => (
                  <div
                    key={idx}
                    className={`capability-item ${openGrowthCap === idx ? "active" : ""}`}
                    onClick={() =>
                      setOpenGrowthCap(openGrowthCap === idx ? -1 : idx)
                    }
                  >
                    <div className="capability-header">
                      <span>{cap.title}</span>
                      <span className="capability-arrow">▶</span>
                    </div>
                    {openGrowthCap === idx && (
                      <div className="capability-body">{cap.summary}</div>
                    )}
                  </div>
                ))}
              </div>

              <Link to="/what-we-do" className="service-explore-btn">
                <span>Explore Growth Engine Playbook</span>
                <span>↗</span>
              </Link>
            </div>

            {/* Service Line 2: India-to-World Platform */}
            <div className="service-line-card gold-theme">
              <div className="service-tag-row">
                <span
                  className="section-badge gold"
                  style={{ marginBottom: 0 }}
                >
                  Global Value & Scale
                </span>
                <span className="service-num">SERVICE 02</span>
              </div>
              <h3 className="service-card-title">India-to-World Platform</h3>
              <p className="service-card-tagline">
                Using India as an origin of global value for regional operating
                hubs, GCCs, product co-creation, manufacturing exports, and
                strategic investment.
              </p>

              <div className="capabilities-list">
                {platformCapabilities.map((cap, idx) => (
                  <div
                    key={idx}
                    className={`capability-item ${openPlatformCap === idx ? "active" : ""}`}
                    onClick={() =>
                      setOpenPlatformCap(openPlatformCap === idx ? -1 : idx)
                    }
                  >
                    <div className="capability-header">
                      <span>{cap.title}</span>
                      <span className="capability-arrow">▶</span>
                    </div>
                    {openPlatformCap === idx && (
                      <div className="capability-body">{cap.summary}</div>
                    )}
                  </div>
                ))}
              </div>

              <Link to="/what-we-do" className="service-explore-btn">
                <span>Explore Platform Capabilities</span>
                <span>↗</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          3. MATURITY STAGES SECTION ("Where are you on the India Journey?")
          ========================================================================== */}
      <section className="stages-section">
        <div className="home-container">
          <div className="section-header">
            <div className="section-badge gold">
              {/* <span className="badge-pulse"></span> */}
              Current Maturity Stage
            </div>
            <h2 className="section-title">
              Where are you on the{" "}
              <span className="gradient-text">India Journey?</span>
            </h2>
            <p className="section-desc">
              Whether you're testing the waters, ready to launch, or already on
              the ground — we provide the structured execution framework to
              guide your next move.
            </p>
          </div>

          {/* Tabs Selector */}
          <div className="stages-tab-nav">
            {stagesData.map((s) => (
              <button
                key={s.stage}
                onClick={() => setActiveStage(s.stage)}
                className={`stage-tab-btn ${activeStage === s.stage ? "active" : ""}`}
              >
                <span className="tab-stage-num">STAGE 0{s.stage}</span>
                <span className="tab-stage-name">{s.name}</span>
              </button>
            ))}
          </div>

          {/* Active Stage Display Card */}
          {currentStageInfo && (
            <div className="stage-display-card">
              <div className="stage-display-grid">
                <div>
                  <div className="stage-card-header">
                    <span className="section-badge">
                      STAGE {currentStageInfo.stage} • {currentStageInfo.sub}
                    </span>
                  </div>
                  <h3 className="stage-title">{currentStageInfo.headline}</h3>
                  <p className="stage-desc">{currentStageInfo.desc}</p>

                  <div className="stage-checklist">
                    {currentStageInfo.checks.map((chk, idx) => (
                      <div key={idx} className="checklist-item">
                        <span className="chk-icon">✓</span>
                        <span>{chk}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Action Box */}
                <div className="stage-action-box">
                  <div className="action-box-tag">NEXT RECOMMENDED STEP</div>
                  <div className="action-box-title">
                    {currentStageInfo.actionTitle}
                  </div>
                  <p className="action-box-desc">
                    {currentStageInfo.actionDesc}
                  </p>
                  <button
                    onClick={() => {
                      setBookingStage(
                        `Stage ${currentStageInfo.stage} — ${currentStageInfo.name}`,
                      );
                      setIsModalOpen(true);
                    }}
                    className="btn-primary-glow"
                    style={{ width: "100%", justifyContent: "center" }}
                  >
                    {currentStageInfo.buttonLabel} ↗
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ==========================================================================
          4. INDIA CONTEXT & MACRO SIGNALS
          ========================================================================== */}
      <section className="context-section">
        <div className="home-container">
          <div className="section-header">
            <div className="section-badge">
              {/* <span className="badge-pulse"></span> */}
              Macro Context & Key Signals
            </div>
            <h2 className="section-title">
              India is one of the{" "}
              <span className="gradient-text">fastest-growing markets</span> -
              and one of the most structurally complex to enter.
            </h2>
            <p className="section-desc">
              Policy-led manufacturing incentives, expanding B2G procurement,
              and a rapidly evolving engineering ecosystem make India a
              strategic priority for global leadership.
            </p>
          </div>

          {/* 4 Pillars Grid */}
          <div className="context-pillars-grid">
            {/* Fastest Growing Market */}
            <div className="context-pillar-card">
              <div className="pillar-icon-box">
                <FaChartLine />
              </div>

              <h3 className="pillar-title">
                One of the Fastest-Growing Markets
              </h3>

              <p className="pillar-desc">
                Surging enterprise, industrial, and consumer purchasing power
                creating massive high-margin demand across 11 key industry
                verticals.
              </p>
            </div>

            {/* Policy Incentives */}
            <div className="context-pillar-card">
              <div className="pillar-icon-box">
                <FaLandmark />
              </div>

              <h3 className="pillar-title">
                Policy-Led Incentives (PLI & SPECS)
              </h3>

              <p className="pillar-desc">
                Government subsidies, tax rebates, and infrastructure schemes
                across electronics, automotive, semiconductors, and green
                energy.
              </p>
            </div>

            {/* Public Sector */}
            <div className="context-pillar-card">
              <div className="pillar-icon-box">
                <FaShoppingCart />
              </div>

              <h3 className="pillar-title">
                Expanding Public-Sector Procurement
              </h3>

              <p className="pillar-desc">
                Mandatory institutional procurement via GeM and central PSUs
                offering billions in qualified enterprise tenders.
              </p>
            </div>

            {/* Export Platform */}
            <div className="context-pillar-card">
              <div className="pillar-icon-box">
                <FaGlobeAsia />
              </div>

              <h3 className="pillar-title">Build, Sell & Export Platform</h3>

              <p className="pillar-desc">
                A dual-vector platform to build products locally, capture
                high-margin domestic revenue, and export cost-competitively
                worldwide.
              </p>
            </div>
          </div>

          {/* Quote Banner */}
          <div className="context-quote-banner">
            <div className="quote-left">
              <div className="quote-text">
                "The companies that succeed in India structure their entry
                around execution discipline, regulatory sequencing, and
                governance rhythm - not market excitement."
              </div>
              <div className="quote-author">
                • scaleAX INDIA STRATEGY PRINCIPLE •
              </div>
            </div>
            <button
              onClick={() => {
                setBookingStage("India Roadmap Strategy");
                setIsModalOpen(true);
              }}
              className="btn-primary-glow"
              style={{ whiteSpace: "nowrap" }}
            >
              View India Roadmap ↗
            </button>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          5. INDUSTRIES WE SERVE (11 Focus Verticals)
          ========================================================================== */}
      <section className="industries-section">
        <div className="home-container">
          <div className="section-header">
            <div className="section-badge gold">
              {/* <span className="badge-pulse"></span> */}
              Industry Verticals
            </div>
            <h2 className="section-title">
              Deep sector expertise across{" "}
              <span className="gradient-text">11 key industries</span>
            </h2>
            <p className="section-desc">
              We specialize in regulated, industrial, and high-growth technology
              sectors where execution complexity and regulatory compliance are
              highest.
            </p>
          </div>

          {/* Filter Bar */}
          <div className="industries-filter-row">
            {[
              "All",
              "Industrial & Regulated",
              "Tech & Electronics",
              "Energy & Infrastructure",
              "Consumer",
            ].map((cat) => (
              <button
                key={cat}
                onClick={() => setIndustryFilter(cat)}
                className={`industry-filter-btn ${industryFilter === cat ? "active" : ""}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Industries Grid */}
          <div className="industries-grid">
            {filteredIndustries.map((ind, idx) => (
              <div key={idx} className="industry-card">
                <div className="ind-icon-wrap">{ind.icon}</div>
                <div className="ind-content">
                  <h4>{ind.name}</h4>
                  <p>{ind.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==========================================================================
          6. ECOSYSTEM & EXECUTION NETWORK
          ========================================================================== */}
      <section className="ecosystem-section">
        <div className="home-container">
          <div className="section-header">
            <div className="section-badge">
              {/* <span className="badge-pulse"></span> */}
              Ecosystem Depth
            </div>
            <h2 className="section-title">
              A network built for{" "}
              <span className="gradient-text">India execution</span>
            </h2>
            <p className="section-desc">
              Government, enterprise, channel, investor, and operating
              relationships — assembled over 15+ years and put to work on your
              behalf from day one.
            </p>
          </div>

          <div className="ecosystem-pillars-grid">
            <div className="eco-pillar-box">
              <div className="eco-role-tag">ACCESS</div>
              <div className="eco-icon-circle">🏛️</div>
              <div className="eco-title">Government</div>
              <div className="eco-desc">
                Central Ministries, RBI, MCA, DPIIT, and State Industrial
                Development Agencies.
              </div>
            </div>

            <div className="eco-pillar-box">
              <div className="eco-role-tag">DEMAND</div>
              <div className="eco-icon-circle">🏢</div>
              <div className="eco-title">Enterprise</div>
              <div className="eco-desc">
                CXO decision-makers, industrial procurement boards, and tier-1
                named accounts.
              </div>
            </div>

            <div className="eco-pillar-box">
              <div className="eco-role-tag">REACH</div>
              <div className="eco-icon-circle">🤝</div>
              <div className="eco-title">Channel</div>
              <div className="eco-desc">
                National distributors, systems integrators, and
                sector-specialized resellers.
              </div>
            </div>

            <div className="eco-pillar-box">
              <div className="eco-role-tag">CAPITAL</div>
              <div className="eco-icon-circle">💼</div>
              <div className="eco-title">Investor</div>
              <div className="eco-desc">
                Private equity funds, cross-border M&A advisors, and strategic
                JV partners.
              </div>
            </div>

            <div className="eco-pillar-box">
              <div className="eco-role-tag">EXECUTION</div>
              <div className="eco-icon-circle">⚙️</div>
              <div className="eco-title">Operations</div>
              <div className="eco-desc">
                Legal counsels, chartered accountants, SEZ authorities, and tech
                hiring networks.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          7. OUTCOMES & CASE STUDIES
          ========================================================================== */}
      <section className="outcomes-section">
        <div className="home-container">
          <div className="section-header">
            <div className="section-badge gold">
              {/* <span className="badge-pulse"></span> */}
              Proven Track Record
            </div>
            <h2 className="section-title">
              Outcome stories from the{" "}
              <span className="gradient-text">India journey</span>
            </h2>
            <p className="section-desc">
              Real results delivered for global enterprises across market entry,
              manufacturing buildout, operations insourcing, and revenue scale.
            </p>
          </div>

          <div className="cases-grid">
            {/* Case 1 */}
            <div className="case-study-card">
              <div>
                <div className="case-top-meta">
                  <span className="case-sector-badge">
                    TELECOM & NETWORK SECURITY
                  </span>
                  <span className="case-service-badge">
                    India-to-World Platform
                  </span>
                </div>
                <h3 className="case-headline">
                  A NYSE-listed US network-security company needed a dedicated
                  India engineering team and center operations run on their
                  behalf.
                </h3>
                <div className="case-metric-highlight">
                  <div className="metric-label">MEASURABLE RESULT</div>
                  <div className="metric-value">
                    Loaded engineer cost reduced from $250K/yr to $35K/yr over 3
                    years; product feature velocity increased 35%.
                  </div>
                </div>
              </div>
              <div className="case-bottom-details">
                <div>
                  • Annual turnover held below 5% with full facility & HR
                  operations governed.
                </div>
                <div>
                  • Parent entity subsequently taken private by a major PE firm
                  post-setup.
                </div>
              </div>
            </div>

            {/* Case 2 */}
            <div className="case-study-card">
              <div>
                <div className="case-top-meta">
                  <span className="case-sector-badge">
                    AUTOMOTIVE & TRANSPORTATION
                  </span>
                  <span className="case-service-badge">
                    Growth Engine + Platform
                  </span>
                </div>
                <h3 className="case-headline">
                  A leading Japanese diesel-engine manufacturer needed to scale
                  capacity, set up an International Procurement Office and build
                  an India plant.
                </h3>
                <div className="case-metric-highlight">
                  <div className="metric-label">MEASURABLE RESULT</div>
                  <div className="metric-value">
                    480 suppliers evaluated down to top 20; land acquisition and
                    40+ regulatory clearances secured for plant buildout.
                  </div>
                </div>
              </div>
              <div className="case-bottom-details">
                <div>
                  • Benchmarked 41 competitors with detailed teardown lab
                  analysis.
                </div>
                <div>
                  • Stood up International Procurement Office (IPO) with 10:1
                  hiring ratio.
                </div>
              </div>
            </div>

            {/* Case 3 */}
            <div className="case-study-card">
              <div>
                <div className="case-top-meta">
                  <span className="case-sector-badge">
                    TELECOM & NETWORKING
                  </span>
                  <span className="case-service-badge">
                    Insourcing & Center Setup
                  </span>
                </div>
                <h3 className="case-headline">
                  A global wholesale telco insourced Customer Service and
                  Network Operations (NOC) from a Tier-1 provider into a new
                  India centre.
                </h3>
                <div className="case-metric-highlight">
                  <div className="metric-label">MEASURABLE RESULT</div>
                  <div className="metric-value">
                    Operations efficiency and customer-experience management
                    enhanced across US, France, and India locations.
                  </div>
                </div>
              </div>
              <div className="case-bottom-details">
                <div>
                  • Remediation of NOC infrastructure, tools, and SLA
                  governance.
                </div>
                <div>
                  • Streamlined governance between telco and international
                  enterprise clients.
                </div>
              </div>
            </div>

            {/* Case 4 */}
            <div className="case-study-card">
              <div>
                <div className="case-top-meta">
                  <span className="case-sector-badge">
                    SEMICONDUCTORS & EMBEDDED
                  </span>
                  <span className="case-service-badge">
                    India Growth Engine
                  </span>
                </div>
                <h3 className="case-headline">
                  A global open-source electronics platform needed full-scope
                  India localization for education and enterprise sectors.
                </h3>
                <div className="case-metric-highlight">
                  <div className="metric-label">MEASURABLE RESULT</div>
                  <div className="metric-value">
                    Local presence established within six months; exceeded
                    revenue objectives through local EMS manufacturing.
                  </div>
                </div>
              </div>
              <div className="case-bottom-details">
                <div>
                  • Localized product strategy and pricing tailored to Indian
                  universities & R&D labs.
                </div>
                <div>
                  • Real-time analytics dashboard implemented for revenue
                  assurance.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          8. CLIENT TESTIMONIALS
          ========================================================================== */}
      <Swiper
        modules={[Autoplay, Pagination]}
        spaceBetween={24}
        slidesPerView={1}
        loop={true}
        speed={1900}
        autoplay={{
          delay: 1000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        pagination={{
          clickable: true,
        }}
        breakpoints={{
          576: {
            slidesPerView: 1,
            spaceBetween: 18,
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          992: {
            slidesPerView: 3,
            spaceBetween: 24,
          },
        }}
        className="testimonials-swiper"
      >
        <SwiperSlide>
          <div className="testimonial-card">
            <div className="testimonial-quote-icon">“</div>

            <p className="testimonial-quote-text">
              "scaleAX assisted us in entering the Indian market by both
              developing operations in our Indian entity over the course of a
              year as well as by helping us acquire a technology partner for our
              R&D team."
            </p>

            <div className="testimonial-author-row">
              <div className="author-info">
                <span className="author-role">Chief Financial Officer</span>

                <span className="author-org">
                  Los Angeles based Visual Effects Co.
                </span>
              </div>

              <span className="author-country-badge">🇺🇸 USA</span>
            </div>
          </div>
        </SwiperSlide>

        <SwiperSlide>
          <div className="testimonial-card">
            <div className="testimonial-quote-icon">“</div>

            <p className="testimonial-quote-text">
              "Our efforts culminated in the founding of our entity in India and
              on-going operations in the region. A huge thank you to our
              incredible partners at scaleAX for making this possible."
            </p>

            <div className="testimonial-author-row">
              <div className="author-info">
                <span className="author-role">Vice President</span>

                <span className="author-org">
                  Multi-national US-based Telecom Co.
                </span>
              </div>

              <span className="author-country-badge">🇺🇸 USA</span>
            </div>
          </div>
        </SwiperSlide>

        <SwiperSlide>
          <div className="testimonial-card">
            <div className="testimonial-quote-icon">“</div>

            <p className="testimonial-quote-text">
              "Their in-depth knowledge of the Indian market has been invaluable
              to us. Furthermore, the breadth of expertise within their teams
              has provided us with insights across multiple domains, proving to
              be an invaluable asset."
            </p>

            <div className="testimonial-author-row">
              <div className="author-info">
                <span className="author-role">APAC Manager</span>

                <span className="author-org">
                  Leading UK-based Payments Company
                </span>
              </div>

              <span className="author-country-badge">🇬🇧 UK</span>
            </div>
          </div>
        </SwiperSlide>

        <SwiperSlide>
          <div className="testimonial-card">
            <div className="testimonial-quote-icon">“</div>

            <p className="testimonial-quote-text">
              "We achieved our Strategic Program Objectives with Innointel's
              partnership across Offshore Vendor Remediation, Capability Center
              setup, and Insourcing programs. They delivered a strong PMO,
              Governance, and total visibility."
            </p>

            <div className="testimonial-author-row">
              <div className="author-info">
                <span className="author-role">Chief Executive Officer</span>

                <span className="author-org">
                  Global Wholesale Telecom Service Provider
                </span>
              </div>

              <span className="author-country-badge">🌐 GLOBAL</span>
            </div>
          </div>
        </SwiperSlide>
      </Swiper>

      {/* ==========================================================================
          9. THE IEO DIFFERENCE (Comparison)
          ========================================================================== */}
      <section className="comparison-section">
        <div className="home-container">
          <div className="section-header">
            <div className="section-badge gold">
              {/* <span className="badge-pulse"></span> */}
              The IEO Advantage
            </div>
            <h2 className="section-title">
              One accountable office vs.{" "}
              <span className="gradient-text">fragmented advisors</span>
            </h2>
            <p className="section-desc">
              Traditional India expansion forces you to juggle multiple
              disconnected vendors. scaleAX provides a single accountable
              program owner who governs every workstream.
            </p>
          </div>

          <div className="comparison-grid">
            {/* Traditional */}
            <div className="comparison-card traditional">
              <div className="comp-header">
                <h3 className="comp-title">The Traditional Model</h3>
                <span className="comp-badge">HIGH RISK & FRAGMENTED</span>
              </div>
              <ul className="comp-list">
                <li>
                  <span className="comp-icon cross">✕</span>
                  <span>
                    Coordinated entirely by internal HQ executives with limited
                    ground bandwidth
                  </span>
                </li>
                <li>
                  <span className="comp-icon cross">✕</span>
                  <span>
                    Separate law firms, tax accountants, real estate brokers,
                    and HR agencies with disconnected goals
                  </span>
                </li>
                <li>
                  <span className="comp-icon cross">✕</span>
                  <span>
                    No unified milestone governance or live executive KPI
                    reporting
                  </span>
                </li>
                <li>
                  <span className="comp-icon cross">✕</span>
                  <span>
                    Advisors charge hefty fees regardless of whether revenue or
                    operational milestones are met
                  </span>
                </li>
                <li>
                  <span className="comp-icon cross">✕</span>
                  <span>
                    Vendor lock-in with ongoing dependency and high recurring
                    costs
                  </span>
                </li>
              </ul>
            </div>

            {/* scaleAX IEO */}
            <div className="comparison-card scaleAX-ieo">
              <div className="comp-header">
                <h3 className="comp-title">scaleAX India Execution Office</h3>
                <span className="comp-badge">SINGLE ACCOUNTABLE OWNER</span>
              </div>
              <ul className="comp-list">
                <li>
                  <span className="comp-icon check">✓</span>
                  <span>
                    One single accountable program partner governing regulatory,
                    commercial, and delivery layers
                  </span>
                </li>
                <li>
                  <span className="comp-icon check">✓</span>
                  <span>
                    Specialist legal, tax, and recruitment partners integrated
                    under one governance charter
                  </span>
                </li>
                <li>
                  <span className="comp-icon check">✓</span>
                  <span>
                    Weekly execution rhythm with live dashboard tracking across
                    HQ and ground teams
                  </span>
                </li>
                <li>
                  <span className="comp-icon check">✓</span>
                  <span>
                    Milestone-driven accountability tied to customer pipeline
                    and tangible regulatory approvals
                  </span>
                </li>
                <li>
                  <span className="comp-icon check">✓</span>
                  <span>
                    100% Asset & IP Transfer: You fully own the entities,
                    pipeline, models, and team at exit
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          10. CLOSING HIGH-IMPACT CTA SECTION
          ========================================================================== */}
      <section className="final-cta-section">
        <div className="home-container">
          <div className="cta-banner-box">
            <div className="section-badge" style={{ margin: "0 auto 20px" }}>
              <span className="badge-pulse"></span>
              Start the India Conversation
            </div>
            <h2 className="cta-banner-title">
              Let us help you build <br />
              <span className="gradient-text">durable revenue in India.</span>
            </h2>
            <p className="cta-banner-desc">
              Book a strategy call and we'll map your fastest, lowest-risk path
              to revenue in India. In one focused session, we map the entry, the
              regulatory path, and the first wins.
            </p>

            <div className="cta-btn-wrapper">
              <button
                onClick={() => {
                  setBookingStage("General Strategy Briefing");
                  setIsModalOpen(true);
                }}
                className="btn-primary-glow"
              >
                Book a Briefing Call <span>↗</span>
              </button>
              <a
                href="mailto:info@scaleAX.com"
                className="btn-secondary-glass"
              >
                Direct Email: info@scaleAX.com <span>✉</span>
              </a>
            </div>

            <div className="cta-guarantee-row">
              <span>✦ Senior Partner Led</span>
              <span>✦ NDA on Request</span>
              <span>✦ Reply within 24 Hours</span>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          11. BRIEFING CALL / STRATEGY MODAL
          ========================================================================== */}
      {isModalOpen && (
        <div
          className="briefing-modal-overlay"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="briefing-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close-btn"
              onClick={() => setIsModalOpen(false)}
            >
              ✕
            </button>

            <div className="modal-header">
              <div className="section-badge">
                <span className="badge-pulse"></span>
                Structured 30-Min Session
              </div>
              <h3>Book an India Strategy Briefing</h3>
              <p>
                Selected Focus:{" "}
                <strong style={{ color: "#0284c7" }}>{bookingStage}</strong>.
                Agenda sent in advance.
              </p>
            </div>

            {formSubmitted ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  background: "rgba(22, 163, 74, 0.08)",
                  borderRadius: "16px",
                  border: "1px solid rgba(22, 163, 74, 0.25)",
                }}
              >
                <div style={{ fontSize: "40px", marginBottom: "14px" }}>✅</div>
                <h4
                  style={{
                    fontSize: "20px",
                    color: "#0f172a",
                    marginBottom: "8px",
                  }}
                >
                  Briefing Request Received
                </h4>
                <p style={{ color: "#475569", fontSize: "14px" }}>
                  A senior partner will reach out within 24 hours with an NDA
                  and calendar invite.
                </p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="e.g. John Doe"
                      value={formData.name}
                      onChange={handleFormChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Your Title *</label>
                    <input
                      type="text"
                      name="title"
                      required
                      placeholder="e.g. VP Global Strategy / CFO"
                      value={formData.title}
                      onChange={handleFormChange}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Work Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={handleFormChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Organisation *</label>
                    <input
                      type="text"
                      name="org"
                      required
                      placeholder="e.g. Acme Global Inc."
                      value={formData.org}
                      onChange={handleFormChange}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Headquarters Location *</label>
                    <input
                      type="text"
                      name="location"
                      required
                      placeholder="e.g. United States / UK / Europe / Japan"
                      value={formData.location}
                      onChange={handleFormChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Primary Service Line</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleFormChange}
                      className="form-select"
                    >
                      <option value="India Growth Engine">
                        India Growth Engine (Sales & GTM)
                      </option>
                      <option value="India-to-World Platform">
                        India-to-World Platform (Hub & GCC)
                      </option>
                      <option value="Both / Full IEO Program">
                        Full IEO Program (End-to-End)
                      </option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Brief Context / Questions (Optional)</label>
                  <textarea
                    name="notes"
                    rows="3"
                    placeholder="Tell us about your industry, current India stage, and key objectives..."
                    value={formData.notes}
                    onChange={handleFormChange}
                    className="form-textarea"
                  ></textarea>
                </div>

                <button type="submit" className="modal-submit-btn">
                  Confirm Briefing Request <span>↗</span>
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Homepage;
